import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';

export default function SubmitQuote() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [quoteData, setQuoteData] = useState({
    line_items: [],
    subtotal: 0,
    tax: 0,
    total: 0,
    delivery_time: '',
    quality_notes: '',
    certifications: []
  });
  
  const [newLineItem, setNewLineItem] = useState({
    supply_id: '',
    supply_name: '',
    unit_price: 0,
    quantity: 1,
    total_price: 0,
    availability: 'in_stock',
    notes: ''
  });

  const [newCertification, setNewCertification] = useState('');

  useEffect(() => {
    if (user && projectId) {
      fetchProjectDetails();
    }
  }, [user, projectId]);

  const fetchProjectDetails = async () => {
    try {
      // We need to get project details from the opportunities endpoint
      const response = await axios.get(`${API}/projects/public/rfq?supplier_id=${user.id}`);
      const foundProject = response.data.find(p => p.id === projectId);
      
      if (!foundProject) {
        alert(i18n.language === 'fr' ? 'Projet non trouvé ou non accessible' : 'Project not found or not accessible');
        navigate('/supplier/opportunities');
        return;
      }
      
      setProject(foundProject);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project:', error);
      alert(i18n.language === 'fr' ? 'Erreur lors du chargement' : 'Error loading project');
      navigate('/supplier/opportunities');
    }
  };

  const handleAddLineItem = () => {
    if (!newLineItem.supply_name || newLineItem.unit_price <= 0 || newLineItem.quantity <= 0) {
      alert(i18n.language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    const total_price = newLineItem.unit_price * newLineItem.quantity;
    const itemToAdd = {
      ...newLineItem,
      total_price: parseFloat(total_price.toFixed(2))
    };

    const updatedLineItems = [...quoteData.line_items, itemToAdd];
    const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total_price, 0);
    const newTotal = newSubtotal + quoteData.tax;

    setQuoteData({
      ...quoteData,
      line_items: updatedLineItems,
      subtotal: parseFloat(newSubtotal.toFixed(2)),
      total: parseFloat(newTotal.toFixed(2))
    });

    setNewLineItem({
      supply_id: '',
      supply_name: '',
      unit_price: 0,
      quantity: 1,
      total_price: 0,
      availability: 'in_stock',
      notes: ''
    });
  };

  const handleRemoveLineItem = (index) => {
    const updatedLineItems = quoteData.line_items.filter((_, i) => i !== index);
    const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total_price, 0);
    const newTotal = newSubtotal + quoteData.tax;

    setQuoteData({
      ...quoteData,
      line_items: updatedLineItems,
      subtotal: parseFloat(newSubtotal.toFixed(2)),
      total: parseFloat(newTotal.toFixed(2))
    });
  };

  const handleTaxChange = (tax) => {
    const taxValue = parseFloat(tax) || 0;
    const newTotal = quoteData.subtotal + taxValue;
    
    setQuoteData({
      ...quoteData,
      tax: parseFloat(taxValue.toFixed(2)),
      total: parseFloat(newTotal.toFixed(2))
    });
  };

  const handleAddCertification = () => {
    if (newCertification.trim()) {
      setQuoteData({
        ...quoteData,
        certifications: [...quoteData.certifications, newCertification.trim()]
      });
      setNewCertification('');
    }
  };

  const handleRemoveCertification = (index) => {
    setQuoteData({
      ...quoteData,
      certifications: quoteData.certifications.filter((_, i) => i !== index)
    });
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();

    if (quoteData.line_items.length === 0) {
      alert(i18n.language === 'fr' ? 'Ajoutez au moins une ligne de devis' : 'Add at least one quote line item');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/projects/quotes/submit?supplier_id=${user.id}`, {
        project_id: projectId,
        ...quoteData
      });

      alert(i18n.language === 'fr' ? 'Devis soumis avec succès !' : 'Quote submitted successfully!');
      navigate('/supplier/opportunities');
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert(i18n.language === 'fr' ? 'Erreur lors de la soumission' : 'Error submitting quote');
      setSubmitting(false);
    }
  };

  const selectSupplyFromProject = (supply) => {
    setNewLineItem({
      ...newLineItem,
      supply_id: supply.id,
      supply_name: supply.name,
      quantity: supply.quantity
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() => navigate('/supplier/opportunities')}
          className="flex items-center gap-2 text-[#0A2540] mb-6 hover:underline"
        >
          <FaArrowLeft />
          {i18n.language === 'fr' ? 'Retour aux opportunités' : 'Back to opportunities'}
        </button>

        <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
          {i18n.language === 'fr' ? 'Soumettre un devis' : 'Submit a Quote'}
        </h1>
        <p className="text-gray-600 mb-8">
          {i18n.language === 'fr' ? 'Pour le projet:' : 'For project:'} <strong>{project.project_name}</strong>
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Project Info Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>{i18n.language === 'fr' ? 'Détails du projet' : 'Project Details'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Localisation' : 'Location'}</p>
                    <p className="font-semibold">{project.location}</p>
                  </div>
                  {project.delivery_deadline && (
                    <div>
                      <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Échéance' : 'Deadline'}</p>
                      <p className="font-semibold">{project.delivery_deadline}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 mb-2">{i18n.language === 'fr' ? 'Fournitures demandées' : 'Required Supplies'}</p>
                    <div className="space-y-2">
                      {project.supplies && project.supplies.map((supply, idx) => (
                        <button
                          key={idx}
                          onClick={() => selectSupplyFromProject(supply)}
                          className="w-full text-left bg-blue-50 hover:bg-blue-100 p-2 rounded text-sm transition"
                        >
                          <p className="font-semibold text-blue-700">{supply.name}</p>
                          <p className="text-xs text-gray-600">{supply.quantity} {supply.unit}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quote Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmitQuote}>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{i18n.language === 'fr' ? 'Lignes de devis' : 'Quote Line Items'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Existing Line Items */}
                  {quoteData.line_items.length > 0 && (
                    <div className="mb-6 space-y-3">
                      {quoteData.line_items.map((item, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold">{item.supply_name}</p>
                            <p className="text-sm text-gray-600">
                              {item.quantity} × {item.unit_price.toFixed(2)}€ = <strong>{item.total_price.toFixed(2)}€</strong>
                            </p>
                            <p className="text-xs text-gray-500">
                              {i18n.language === 'fr' ? 'Disponibilité:' : 'Availability:'} {item.availability}
                            </p>
                            {item.notes && <p className="text-xs text-gray-500 mt-1">{item.notes}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(index)}
                            className="text-red-500 hover:text-red-700 ml-4"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Line Item */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-3 text-[#0A2540]">
                      {i18n.language === 'fr' ? 'Ajouter une ligne' : 'Add Line Item'}
                    </h4>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="col-span-2">
                        <label className="block text-sm font-semibold mb-1">
                          {i18n.language === 'fr' ? 'Nom de la fourniture *' : 'Supply Name *'}
                        </label>
                        <input
                          type="text"
                          value={newLineItem.supply_name}
                          onChange={(e) => setNewLineItem({...newLineItem, supply_name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={i18n.language === 'fr' ? 'Ex: Béton C25' : 'Ex: Concrete C25'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">
                          {i18n.language === 'fr' ? 'Prix unitaire (€) *' : 'Unit Price (€) *'}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newLineItem.unit_price}
                          onChange={(e) => setNewLineItem({...newLineItem, unit_price: parseFloat(e.target.value) || 0})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">
                          {i18n.language === 'fr' ? 'Quantité *' : 'Quantity *'}
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={newLineItem.quantity}
                          onChange={(e) => setNewLineItem({...newLineItem, quantity: parseInt(e.target.value) || 1})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">
                          {i18n.language === 'fr' ? 'Disponibilité' : 'Availability'}
                        </label>
                        <select
                          value={newLineItem.availability}
                          onChange={(e) => setNewLineItem({...newLineItem, availability: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                          <option value="in_stock">{i18n.language === 'fr' ? 'En stock' : 'In Stock'}</option>
                          <option value="1_week">{i18n.language === 'fr' ? '1 semaine' : '1 Week'}</option>
                          <option value="2_weeks">{i18n.language === 'fr' ? '2 semaines' : '2 Weeks'}</option>
                          <option value="1_month">{i18n.language === 'fr' ? '1 mois' : '1 Month'}</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-semibold mb-1">
                          {i18n.language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
                        </label>
                        <input
                          type="text"
                          value={newLineItem.notes}
                          onChange={(e) => setNewLineItem({...newLineItem, notes: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddLineItem}
                      className="w-full bg-[#635BFF] text-white px-4 py-2 rounded-lg hover:bg-[#5248d9] flex items-center justify-center gap-2"
                    >
                      <FaPlus />
                      {i18n.language === 'fr' ? 'Ajouter cette ligne' : 'Add This Line'}
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Pricing Summary & Additional Info */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{i18n.language === 'fr' ? 'Récapitulatif et informations' : 'Summary & Information'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        {i18n.language === 'fr' ? 'Sous-total' : 'Subtotal'}
                      </label>
                      <input
                        type="text"
                        value={`${quoteData.subtotal.toFixed(2)}€`}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        {i18n.language === 'fr' ? 'Taxes (€)' : 'Tax (€)'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={quoteData.tax}
                        onChange={(e) => handleTaxChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                  <div className="mb-4 p-4 bg-[#635BFF]/5 rounded-lg">
                    <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Total' : 'Total'}</p>
                    <p className="text-3xl font-bold text-[#635BFF]">{quoteData.total.toFixed(2)}€</p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">
                      {i18n.language === 'fr' ? 'Délai de livraison' : 'Delivery Time'}
                    </label>
                    <input
                      type="text"
                      value={quoteData.delivery_time}
                      onChange={(e) => setQuoteData({...quoteData, delivery_time: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder={i18n.language === 'fr' ? 'Ex: 2 semaines' : 'Ex: 2 weeks'}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">
                      {i18n.language === 'fr' ? 'Notes qualité' : 'Quality Notes'}
                    </label>
                    <textarea
                      value={quoteData.quality_notes}
                      onChange={(e) => setQuoteData({...quoteData, quality_notes: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows="3"
                      placeholder={i18n.language === 'fr' ? 'Décrivez la qualité de vos produits...' : 'Describe your product quality...'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      {i18n.language === 'fr' ? 'Certifications' : 'Certifications'}
                    </label>
                    {quoteData.certifications.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {quoteData.certifications.map((cert, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                            {cert}
                            <button
                              type="button"
                              onClick={() => handleRemoveCertification(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCertification}
                        onChange={(e) => setNewCertification(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCertification())}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                        placeholder={i18n.language === 'fr' ? 'Ex: ISO 9001' : 'Ex: ISO 9001'}
                      />
                      <button
                        type="button"
                        onClick={handleAddCertification}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                      >
                        {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || quoteData.line_items.length === 0}
                className="w-full bg-[#635BFF] text-white px-6 py-4 rounded-lg hover:bg-[#5248d9] font-bold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting 
                  ? (i18n.language === 'fr' ? 'Envoi en cours...' : 'Submitting...')
                  : (i18n.language === 'fr' ? '✅ Soumettre le devis' : '✅ Submit Quote')
                }
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
